import React, { useEffect, useRef, useState } from 'react';
import { openErrorToaster } from 'services/toast.service';
import { useBagelChat, BagelChatAttachment } from '../../context/BagelChatContext';
import useAutoScroll from '../../hooks/useAutoScroll';
import {
  uploadBagelChatImage,
  BagelChatUploadedImage,
} from '../../../api/bagel-chat-upload';
import BagelMessage from './BagelMessage';
import BagelConversationHistory from './BagelConversationHistory';
import styles from './BagelChatWidget.module.scss';

const DEFAULT_CHIPS = ['How does Bagel AI work?'];

function WelcomeState({ onChip }: { onChip: (text: string) => void }) {
  const chips = DEFAULT_CHIPS;
  return (
    <div className={styles.welcome}>
      <div className={styles.welcomeAvatar}>B</div>
      <p className={styles.welcomeTitle}>Bagel AI</p>
      <p className={styles.welcomeSubtitle}>
        Ask me anything about URBAN TXT programs, SCLA, or how to use
        Rosie.
      </p>
      <div className={styles.chips}>
        {chips.map(chip => (
          <button
            key={chip}
            className={styles.chip}
            type="button"
            onClick={() => onChip(chip)}
          >
            {chip}
          </button>
        ))}
      </div>
    </div>
  );
}

type UploadStatus = 'uploading' | 'done' | 'error';

interface PendingAttachment {
  file: File;
  previewUrl: string;
  status: UploadStatus;
  result?: BagelChatUploadedImage;
  error?: string;
}

function AttachmentChip({
  attachment,
  onRemove,
}: {
  attachment: PendingAttachment;
  onRemove: () => void;
}) {
  return (
    <div className={styles.attachmentChip}>
      <img
        src={attachment.previewUrl}
        alt={attachment.file.name}
        className={styles.attachmentThumb}
      />
      <div className={styles.attachmentInfo}>
        <span className={styles.attachmentFilename}>{attachment.file.name}</span>
        {attachment.status === 'uploading' && (
          <span className={styles.attachmentStatus}>Uploading…</span>
        )}
        {attachment.status === 'error' && (
          <span className={styles.attachmentError}>
            {attachment.error ?? 'Upload failed'}
          </span>
        )}
      </div>
      <button
        type="button"
        className={styles.attachmentRemoveBtn}
        onClick={onRemove}
        aria-label="Remove attached image"
        title="Remove attached image"
      >
        ✕
      </button>
    </div>
  );
}

function ChatInput({
  onSend,
  disabled,
}: {
  onSend: (text: string, attachment?: BagelChatAttachment) => void;
  disabled: boolean;
}) {
  const [value, setValueRaw] = useState('');
  const [attachment, setAttachment] = useState<PendingAttachment | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const setValue = (v: string) => {
    setValueRaw(v);
    // auto-grow
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  };

  // Revoke the local preview object URL whenever it's replaced or the component unmounts.
  useEffect(() => {
    const url = attachment?.previewUrl;
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [attachment?.previewUrl]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file later
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      openErrorToaster('Only image files can be attached.', 5000);
      return;
    }

    const previewUrl = URL.createObjectURL(file);
    setAttachment({ file, previewUrl, status: 'uploading' });

    uploadBagelChatImage(file)
      .then(result => {
        setAttachment(prev =>
          prev && prev.file === file ? { ...prev, status: 'done', result } : prev,
        );
      })
      .catch((err: unknown) => {
        const message =
          err instanceof Error ? err.message : 'Image upload failed.';
        setAttachment(prev =>
          prev && prev.file === file ? { ...prev, status: 'error', error: message } : prev,
        );
      });
  };

  const handleRemoveAttachment = () => setAttachment(null);

  const isUploading = attachment?.status === 'uploading';
  const canSend = (!!value.trim() || attachment?.status === 'done') && !isUploading;

  const handleSend = () => {
    if (!canSend || disabled) return;

    const sentAttachment: BagelChatAttachment | undefined =
      attachment?.status === 'done' && attachment.result?.url
        ? {
            url: attachment.result.url,
            filename: attachment.file.name,
            mediaType: attachment.file.type,
          }
        : undefined;

    onSend(value.trim(), sentAttachment);
    setValue('');
    setAttachment(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className={styles.composer}>
      {attachment && (
        <AttachmentChip attachment={attachment} onRemove={handleRemoveAttachment} />
      )}
      <div className={styles.inputArea}>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className={styles.hiddenFileInput}
          onChange={handleFileChange}
          aria-hidden="true"
          tabIndex={-1}
          data-testid="bagel-chat-attach-input"
        />
        <button
          type="button"
          className={styles.attachBtn}
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled}
          aria-label="Attach an image"
          title="Attach an image"
        >
          📎
        </button>
        <textarea
          ref={textareaRef}
          className={styles.textarea}
          rows={1}
          value={value}
          onChange={e => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask Bagel AI…"
          disabled={disabled}
          aria-label="Chat message input"
        />
        <button
          type="button"
          className={styles.sendBtn}
          onClick={handleSend}
          disabled={!canSend || disabled}
          aria-label="Send message"
        >
          ↑
        </button>
      </div>
    </div>
  );
}

export default function BagelChatWidget() {
  const {
    isOpen,
    messages,
    isStreaming,
    streamingMessageId,
    activeWorkflowId,
    conversations,
    isLoadingConversations,
    showHistory,
    setShowHistory,
    toggleOpen,
    close,
    send,
    startNewConversation,
    loadConversation,
    refreshConversations,
    downloadConversation,
  } = useBagelChat();

  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const { isAtBottom, scrollToBottom } = useAutoScroll({
    messagesContainerRef,
    messages,
  });

  // Refresh history when opening the panel
  useEffect(() => {
    if (showHistory) {
      refreshConversations();
    }
  }, [showHistory, refreshConversations]);

  // Close on Escape — history panel first, then chat window
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || !isOpen) return;
      if (showHistory) {
        setShowHistory(false);
        return;
      }
      close();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, showHistory, close, setShowHistory]);

  return (
    <>
      {/* Floating toggle button */}
      <button
        type="button"
        className={styles.toggle}
        onClick={toggleOpen}
        aria-label={isOpen ? 'Close Bagel AI chat' : 'Open Bagel AI chat'}
        title="Bagel AI"
      >
        {isOpen ? '✕' : '🥯'}
      </button>

      {/* Chat window */}
      {isOpen && (
        <div
          className={styles.window}
          role="dialog"
          aria-label="Bagel AI Chat"
          aria-modal="false"
        >
          {/* Header */}
          <div className={styles.header}>
            <button
              type="button"
              className={`${styles.headerBtn} ${styles.headerHistoryBtn} ${
                showHistory ? styles.headerBtnActive : ''
              }`}
              onClick={() => setShowHistory(!showHistory)}
              title="Past conversations"
              aria-label="Past conversations"
            >
              ☰
            </button>
            <div className={styles.headerAvatar}>B</div>
            <div className={styles.headerInfo}>
              <div className={styles.headerTitle}>Bagel AI</div>
              <div className={styles.headerStatus}>Online</div>
            </div>
            <div className={styles.headerActions}>
              <button
                type="button"
                className={styles.headerBtn}
                onClick={downloadConversation}
                disabled={messages.length === 0}
                title="Download conversation (JSON)"
                aria-label="Download conversation as JSON"
              >
                ↓
              </button>
              <button
                type="button"
                className={styles.headerBtn}
                onClick={startNewConversation}
                title="New conversation"
                aria-label="New conversation"
              >
                ✎
              </button>
              <button
                type="button"
                className={styles.headerBtn}
                onClick={close}
                title="Close"
                aria-label="Close chat"
              >
                ✕
              </button>
            </div>
          </div>

          {showHistory ? (
            <BagelConversationHistory
              conversations={conversations}
              activeWorkflowId={activeWorkflowId}
              isLoading={isLoadingConversations}
              onSelect={loadConversation}
              onNewConversation={startNewConversation}
              onClose={() => setShowHistory(false)}
            />
          ) : (
            <>
              {/* Messages area + scroll-to-bottom overlay */}
              <div className={styles.messagesWrapper}>
                <div
                  ref={messagesContainerRef}
                  className={styles.messages}
                  aria-live="polite"
                >
                  {messages.length === 0 ? (
                    <WelcomeState onChip={text => send(text)} />
                  ) : (
                    messages.map(msg => (
                      <BagelMessage
                        key={
                          msg.clientId ??
                          `${msg.role}-${msg.content.slice(0, 20)}`
                        }
                        message={msg}
                        isStreaming={
                          !!streamingMessageId &&
                          msg.clientId === streamingMessageId
                        }
                      />
                    ))
                  )}
                </div>

                {/* Down-chevron — appears when content is below the viewport */}
                {!isAtBottom && (
                  <button
                    type="button"
                    className={styles.scrollDownBtn}
                    onClick={() => scrollToBottom()}
                    aria-label="Scroll to bottom"
                    title="Scroll to bottom"
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>
                )}
              </div>

              <ChatInput onSend={send} disabled={isStreaming} />
            </>
          )}
        </div>
      )}
    </>
  );
}
